import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import PageHeader from '@/components/PageHeader';

type LanguageOption = 'pt' | 'en' | 'es';

const languageLabels = {
  pt: 'Português 🇧🇷',
  en: 'English 🇺🇸',
  es: 'Español 🇪🇸',
};

export default function LanguageSettingsScreen() {
  const router = useRouter();
  const { language: currentLanguage, setLanguage, t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(currentLanguage);

  const handleSave = async () => {
    await setLanguage(selectedLanguage);
    // PRD §5.10 — `settings_changed` (`field=language`). Success path only.
    import('@/utils/clarityEvents')
      .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'language' }))
      .catch(() => {});
    router.back();
  };

  const renderLanguageOption = (lang: LanguageOption) => {
    const isSelected = selectedLanguage === lang;

    return (
      <TouchableOpacity
        key={lang}
        style={[
          styles.card,
          isSelected && styles.cardSelected,
        ]}
        onPress={() => setSelectedLanguage(lang)}
        activeOpacity={0.7}
      >
        <Text style={styles.languageText}>{languageLabels[lang]}</Text>
        <View style={[styles.radioButton, isSelected && styles.radioButtonSelected]}>
          {isSelected && <View style={styles.radioButtonInner} />}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader title={t('account.languageSettings.title')} showBackButton onBack={() => router.back()} backgroundColor="#F9F8F3" />

      <View style={styles.body}>
        <View style={styles.optionsContainer}>
          {renderLanguageOption('pt')}
          {renderLanguageOption('en')}
          {renderLanguageOption('es')}
        </View>
      </View>

      <View style={styles.buttonSection}>
        <TouchableOpacity
          style={styles.button}
          onPress={handleSave}
          activeOpacity={0.8}
        >
          <Text style={styles.buttonText}>{t('account.languageSettings.button')}</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  body: {
    flex: 1,
    padding: 24,
  },
  optionsContainer: {
    gap: 12,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    height: 64,
  },
  cardSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  languageText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#151515',
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#FFFFFF',
  },
  radioButtonInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#015480',
  },
  buttonSection: {
    padding: 24,
    width: '100%',
  },
  button: {
    height: 48,
    borderRadius: 12,
    backgroundColor: '#151515',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  buttonText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#FFFFFF',
    textAlign: 'center',
  },
});
